import { createFileRoute } from "@tanstack/react-router";
import { SiteHeaderEn } from "@/components/SiteHeaderEn";
import { SiteFooterEn } from "@/components/SiteFooterEn";
import { LeadSection } from "@/components/LeadSection";

const SITE = "https://shopify-freelancer.net";
const TITLE = "My Method: Diagnosis Before Treatment | Shopify Freelancer";
const DESCRIPTION =
  "How a Shopify conversion sprint runs: diagnosis from real data, prioritised interventions, implementation in your theme and a before/after check on the same metrics.";

export const Route = createFileRoute("/en/method")({
  component: MethodEn,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}/en/method` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/en/method` },
      { rel: "alternate", hrefLang: "de", href: "https://shopify-freelancer.net/methode" },
      { rel: "alternate", hrefLang: "en", href: "https://shopify-freelancer.net/en/method" },
      { rel: "alternate", hrefLang: "x-default", href: "https://shopify-freelancer.net/methode" },
      { rel: "alternate", hrefLang: "de", href: `${SITE}/methode` },
      { rel: "alternate", hrefLang: "en", href: `${SITE}/en/method` },
    ],
  }),
});

const steps = [
  {
    n: "01",
    h: "Diagnosis",
    p: "Analytics, session recordings, funnel and checkout data, speed measurements. The output is a written picture of where your store loses money and how much each leak is worth.",
  },
  {
    n: "02",
    h: "Priorities",
    p: "Not everything that can be improved is worth improving. We agree on the handful of interventions with the largest expected effect on revenue per session.",
  },
  {
    n: "03",
    h: "Implementation",
    p: "Changes are built directly in your theme, staged first, reviewed together, then shipped. No unfinished experiments left in the live store.",
  },
  {
    n: "04",
    h: "Verification",
    p: "Same metrics, before and after: conversion rate overall and on mobile, average order value, revenue per session. The result is either visible in the numbers or it is not a result.",
  },
];

function MethodEn() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeaderEn />
      <main>
        <section className="veil border-b border-border/60 py-16 md:py-20">
          <div className="mx-auto max-w-6xl px-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.34em] text-primary">
              Method
            </p>
            <h1 className="mt-5 max-w-3xl text-4xl font-bold leading-[1.08] md:text-5xl">
              Diagnosis before <span className="text-signal">treatment</span>
            </h1>
            <p className="mt-5 max-w-xl text-muted-foreground">
              A store is not redesigned on taste. It is measured, understood, changed and measured
              again.
            </p>
          </div>
        </section>

        <section className="py-14">
          <div className="mx-auto grid max-w-5xl gap-6 px-5 md:grid-cols-2">
            {steps.map((s) => (
              <div key={s.n} className="rounded-lg border border-border bg-card p-6">
                <span className="font-mono text-xs tracking-[0.3em] text-primary">{s.n}</span>
                <h2 className="mt-3 text-xl font-semibold">{s.h}</h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.p}</p>
              </div>
            ))}
          </div>

          <div className="mx-auto mt-12 max-w-3xl px-5">
            <h2 className="text-2xl font-bold md:text-3xl">What I will not do</h2>
            <p className="mt-4 leading-relaxed text-muted-foreground">
              I do not sell hours, I do not redesign stores that convert fine, and I do not start a
              sprint when the numbers say it will not pay for itself. In that case you get the
              reasoning and keep your budget.
            </p>
          </div>
        </section>

        <LeadSection source="en-method" lang="en" />
      </main>
      <SiteFooterEn />
    </div>
  );
}
